import type {
  SeedingCampaignKind,
  SeedingCampaignStatus,
  SeedingProposeStatus,
  SeedingRecruitStatus,
} from '../types';

// PRD §1-1/§1-2 — 제안형 preparing 부터 콘텐츠 제작 가이드 시트 우선 노출.
// 다른 status 적용 여부는 디자이너/PO 컨펌 후 추가 예정.
const CONTENT_GUIDE_SHEET_STATUSES = new Set<SeedingCampaignStatus>(['preparing']);

export const shouldShowContentGuideSheet = (
  kind: SeedingCampaignKind,
  status: SeedingCampaignStatus,
): boolean => kind === 'propose' && CONTENT_GUIDE_SHEET_STATUSES.has(status);

// 모집형 ----------------------------------------------------------------

// 회색 캠페인 정보 카드 (콘텐츠 유형 안내)
// Figma — F6-1: delivered, contentRegistered 에서 카드 미노출 (상단 영역이 countdown H1 + Card + ListTable 컨테이너로 묶임)
export const RECRUIT_STATUSES_WITH_INFO_CARD: SeedingRecruitStatus[] = [
  'recruiting',
  'applied',
  'selected',
  'shipping',
  'closedSelecting',
];

// 상품 카드의 카테고리 라벨 노출 — F5-5
// recruiting/applied 만 (신청 단계). 진행 단계 + 결과 단계에서는 미노출.
// closedSelecting (신청 내역) 도 신청 단계 연속선상이라 포함.
// 신청 완료(applied) 는 카테고리 타이틀 없이 상품 리스트만 노출. (SID-4326 수정3-2)
export const RECRUIT_STATUSES_WITH_PRODUCT_CATEGORY: SeedingRecruitStatus[] = [
  'recruiting',
  'closedSelecting',
];

// countdown H1 (TitleSection 위치의 큰 typography) — F5-1b, F6-1
// delivered/contentRegistered 에선 "콘텐츠 등록까지 남은 시간 D-N" 이 H1 자리.
// applied/closedSelecting 의 "선정까지 남은 시간"도 카드가 아닌 동일한 H1 타이틀로 노출. (SID-4212)
export const RECRUIT_STATUSES_WITH_COUNTDOWN_HEADER: SeedingRecruitStatus[] = [
  'applied',
  'closedSelecting',
  'delivered',
  'contentRegistered',
];

// 신청 번호 노출 — 콘텐츠 제작 가이드 영역 내부 행. selected~contentRegistered 만 (rejected 제외)
export const RECRUIT_STATUSES_WITH_APPLICATION_NUMBER: SeedingRecruitStatus[] = [
  'selected',
  'shipping',
  'delivered',
  'contentRegistered',
];

// 콘텐츠 제작 가이드 info ⓘ 아이콘 — 신청 페이지(recruiting) + 신청 후 단계 + 진행 단계 전체.
// recruiting(신청 전)에서도 제작 가이드 시트를 열 수 있어야 함. (CMQA-7100)
export const RECRUIT_STATUSES_WITH_GUIDE_INFO_ICON: SeedingRecruitStatus[] = [
  'recruiting',
  'applied',
  'selected',
  'shipping',
  'delivered',
  'contentRegistered',
  'closedSelecting',
  'rejected',
];

// ProgressStatusHeader 노출 status — F5-1b/F6-1 delivered/contentRegistered 는 countdown H1 로 대체
export const RECRUIT_STATUSES_WITH_PROGRESS_HEADER: SeedingRecruitStatus[] = [
  'selected',
  'shipping',
  'rejected',
];

// 제안형 ----------------------------------------------------------------

// countdown header (큰 H1) — F12-1 + F14 매핑 + soldOut (F18 — 전체 품절도 countdown H1)
export const PROPOSE_STATUSES_WITH_COUNTDOWN_HEADER: SeedingProposeStatus[] = [
  'pending',
  'productSelected',
  'delivered',
  'contentRegistered',
  'soldOut',
];

// ProgressStatusHeader (제목 H1 "상품 준비 중" / "상품 배송 중" / 거절="거절") — F14-5 / SID-4210
export const PROPOSE_STATUSES_WITH_PROGRESS_HEADER: SeedingProposeStatus[] = [
  'preparing',
  'shipping',
  'rejected',
];

// 회색 캠페인 정보 카드 — F14-1: preparing/shipping 추가
export const PROPOSE_STATUSES_WITH_INFO_CARD: SeedingProposeStatus[] = [
  'pending',
  'productSelected',
  'preparing',
  'shipping',
  'soldOut',
];

// 콘텐츠 제작 가이드 info ⓘ 아이콘 (제안형) — 수락 완료(preparing) 이후 상세부터 활성. (SID-4150)
// 기존엔 shippingTracking 데이터 유무에 묶여 preparing 단계에서 아이콘이 누락됐다.
export const PROPOSE_STATUSES_WITH_GUIDE_INFO_ICON: SeedingProposeStatus[] = [
  'preparing',
  'shipping',
  'delivered',
  'contentRegistered',
];

// 배송지 정보 editable — 제안형은 수락 전(pending/productSelected)까지만 변경 가능.
// 수락 시 주문 생성 → preparing 부터 불가. (SID-4116 reopen — 'preparing' 제거)
// (어댑터 EDITABLE_ADDRESS_STATUSES 와 정합)
export const PROPOSE_STATUSES_WITH_EDITABLE_SHIPPING_ADDRESS: SeedingProposeStatus[] = [
  'pending',
  'productSelected',
];
